import {
  Clock,
  Keyboard,
  Layers as LayersIcon,
  Loader2,
  Monitor,
  Smartphone,
  Tablet,
  Timer,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { useSyncStatus, useTemporal } from '@/store/editor.store';

export function BottomBar() {
  const { status } = useSyncStatus();
  const [time, setTime] = useState(() => formatTime());
  const pastStates = useTemporal((s) => s.pastStates.length);
  const futureStates = useTemporal((s) => s.futureStates.length);

  useEffect(() => {
    if (status === 'idle') setTime(formatTime());
  }, [status]);

  return (
    <footer className="flex h-9 shrink-0 items-center justify-between border-t border-border bg-bg-panel px-5 text-[11px] text-fg-muted">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5">
          {status === 'saving' || status === 'pending' ? (
            <>
              <Loader2 size={11} className="animate-spin text-accent" />
              SAVING…
            </>
          ) : status === 'error' ? (
            <span className="text-red-500">SAVE FAILED</span>
          ) : (
            <>
              <Clock size={11} />
              AUTOSAVED AT {time}
            </>
          )}
        </span>
        <span className="text-fg-subtle">•</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          Connected
        </span>
        <span className="text-fg-subtle">•</span>
        <span className="text-fg-subtle">
          {pastStates} undo · {futureStates} redo
        </span>
      </div>

      <div className="flex items-center gap-4">
        <FootLink icon={Timer}>TIMELINE</FootLink>
        <FootLink icon={LayersIcon}>REVEAL STEPS</FootLink>
        <FootLink icon={Keyboard}>SHORTCUTS</FootLink>
        <div className="ml-2 flex items-center gap-1 rounded-md border border-border bg-bg-subtle p-0.5">
          <DeviceBtn icon={Monitor} active title="Desktop" />
          <DeviceBtn icon={Tablet} title="Tablet" />
          <DeviceBtn icon={Smartphone} title="Mobile" />
        </div>
      </div>
    </footer>
  );
}

function FootLink({
  icon: Icon,
  children,
}: {
  icon: typeof Clock;
  children: React.ReactNode;
}) {
  return (
    <button className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-fg-subtle transition hover:text-fg">
      <Icon size={11} />
      {children}
    </button>
  );
}

function DeviceBtn({
  icon: Icon,
  title,
  active,
}: {
  icon: typeof Monitor;
  title: string;
  active?: boolean;
}) {
  return (
    <button
      title={title}
      className={`grid h-6 w-6 place-items-center rounded transition ${
        active ? 'bg-white text-fg shadow-pill' : 'text-fg-subtle hover:text-fg'
      }`}
    >
      <Icon size={12} />
    </button>
  );
}

function formatTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
